import React from "react";
import { PlatformIcon, PlatformWordmark } from "@/components/brand/PlatformWordmark";

/**
 * Branded splash used everywhere React needs to say "hang on".
 *
 * Renders as an fdfcf8 (cream) full-screen block with the AIFLedger monogram
 * fading + a gold "reveal" dot pulse — matches the boot splash in
 * `public/index.html` so users don't see two different loaders in a session.
 *
 * Props:
 *   compact = true  → use in-shell (min-h-[50vh]) instead of full-screen
 *   message         → optional line under the logo (defaults to none)
 */
export default function SplashLoader({ compact = false, message = "" }) {
  const wrapper = compact
    ? "min-h-[50vh] w-full flex flex-col items-center justify-center bg-[#fdfcf8]"
    : "fixed inset-0 z-50 flex flex-col items-center justify-center bg-[#fdfcf8]";
  return (
    <div className={wrapper} data-testid="splash-loader" role="status" aria-live="polite">
      <div className="flex items-center gap-3 select-none">
        <PlatformIcon className="h-11 w-11 rounded-lg splash-fade" width={44} height={44} />
        <PlatformWordmark className="h-8 splash-fade" />
      </div>
      <div className="mt-6 flex items-center gap-1.5" aria-hidden="true">
        <span className="splash-pip" style={{ animationDelay: "0ms" }} />
        <span className="splash-pip" style={{ animationDelay: "180ms" }} />
        <span className="splash-pip" style={{ animationDelay: "360ms" }} />
      </div>
      {message ? (
        <div className="mt-4 text-[11px] uppercase tracking-[0.24em] text-[#475569] font-semibold">
          {message}
        </div>
      ) : null}
      <style>{`
        .splash-fade { animation: splash-in 900ms ease-out both; }
        .splash-dot  { animation: splash-dot 1400ms ease-in-out infinite; display:inline-block; transform-origin:center bottom; }
        .splash-pip {
          width:6px; height:6px; border-radius:9999px; background:#c4a454;
          display:inline-block; opacity:0.35;
          animation: splash-pip 1200ms ease-in-out infinite;
        }
        @keyframes splash-in  { 0% { opacity:0; transform: translateY(4px); } 100% { opacity:1; transform: none; } }
        @keyframes splash-dot { 0%,100% { transform: translateY(0)   scale(1);   opacity:0.9; }
                                 50%     { transform: translateY(-3px) scale(1.1); opacity:1; } }
        @keyframes splash-pip { 0%,100% { opacity:0.25; transform: scale(0.8); }
                                 50%     { opacity:1;    transform: scale(1.1); } }
      `}</style>
    </div>
  );
}
